import { FileText, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export type NoteExportFormat = "md" | "pdf";

/**
 * Lightweight export dialog for notes. Two formats (Markdown, PDF) +
 * filename input. Deliberately simpler than the canvas-kit ExportDialog
 * because notes have no pagination/paper/scale knobs — they always export
 * the full document.
 */
export function NoteExportDialog({
  defaultFilename,
  defaultFormat = "pdf",
  onConfirm,
  onClose,
}: {
  defaultFilename: string;
  defaultFormat?: NoteExportFormat;
  onConfirm: (args: { format: NoteExportFormat; filename: string }) => Promise<void> | void;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<NoteExportFormat>(defaultFormat);
  const [filename, setFilename] = useState(defaultFilename);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        format,
        filename: filename.trim() || defaultFilename,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Notiz exportieren"
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="glass-floating w-[min(380px,calc(100vw-32px))] rounded-3xl p-4 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-body-sm font-semibold uppercase tracking-wider">Notiz exportieren</h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Schließen"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
          >
            <X className="size-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) void submit();
          }}
          className="flex flex-col gap-3"
        >
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Format
            </span>
            <div className="flex h-10 overflow-hidden rounded-2xl border border-white/15 bg-white/4">
              {(["pdf", "md"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  disabled={busy}
                  onClick={() => setFormat(f)}
                  className={
                    "flex flex-1 items-center justify-center gap-1.5 text-body-sm uppercase tracking-wider transition-colors " +
                    (format === f ? "bg-pitch-lime/20 text-pitch-lime" : "hover:bg-white/10")
                  }
                >
                  <FileText className="size-3.5" />
                  {f}
                </button>
              ))}
            </div>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Dateiname
            </span>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              disabled={busy}
              className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
            />
          </label>

          <div className="rounded-2xl border border-white/10 bg-white/2 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
            {format === "pdf"
              ? "Exportiert die Notiz als A4-PDF mit Überschriften, Listen, Code & Links."
              : "Exportiert die Notiz als reine Markdown-Datei (.md)."}
          </div>

          {error && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-body-sm text-red-200">
              {error}
            </div>
          )}

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => !busy && onClose()}
              disabled={busy}
              className="h-10 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm hover:bg-white/10 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Exportiere…" : "Exportieren"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
